import React from 'react'
import { createPortal } from 'react-dom'
import { useDialog } from '../../hooks/useDialog'
import type { FreedomCard } from '../../types/app'
import {
  FREEDOM_ABOUT,
  FREEDOM_ABOUT_TITLE,
  FREEDOM_GOING_WELL,
  FREEDOM_HOW_SCORED,
  FREEDOM_STARTS,
  FREEDOM_WORK_ON,
  freedomPartPeriod,
  freedomPoints,
  freedomScoredOn,
} from '../../plan/wording'
import { Icon, IconButton } from '../../ui/Icon'
import { InfoTip } from '../../ui/InfoTip'
import '../../components/Sheet.css'

interface Props {
  freedom: FreedomCard | null
  // bare drops the card around it, for a row inside the This week card.
  bare?: boolean
}

// FreedomLine is the last completed week's Freedom Score: a row in the This
// week card once there's a score, a card of its own at the foot of Goals
// before then. There's nothing to show before her first payment or expense
// (freedom is null then). Once there's a score, or a list of what it still
// needs, the whole row is one button with a chevron that opens the
// breakdown in a sheet, so the page never grows by the length of the list;
// while it's only waiting, it isn't a button. A score from fewer than all
// five parts says so beside it. An ⓘ beside the row, in every state, opens
// a bubble saying what the score is.
export default function FreedomLine({ freedom, bare = false }: Props) {
  const [open, setOpen] = React.useState(false)

  if (!freedom) {
    return null
  }
  const lineClass = bare ? 'freedom-line freedom-line--bare' : 'freedom-line'

  if (freedom.waiting) {
    return (
      <div className={lineClass}>
        <div className="freedom-line__head">
          <div className="freedom-line__summary">
            <span className="freedom-line__text">
              <span className="freedom-line__label">Freedom Score</span>{' '}
              <span className="freedom-line__starts">{FREEDOM_STARTS}</span>
            </span>
          </div>
          <InfoTip title={FREEDOM_ABOUT_TITLE} lines={FREEDOM_ABOUT} className="freedom-about" />
        </div>
      </div>
    )
  }

  // enough means at least 2 parts are measured, so the early note never says
  // fewer than that.
  const measured = freedom.parts.filter((part) => part.measured).length
  const summary = freedom.enough ? (
    <>
      <span className="freedom-line__label">Freedom Score</span>{' '}
      <span className="freedom-score-row">
        <span className="freedom-score">{freedom.score}</span>{' '}
        {freedom.change && <span className="freedom-change">{freedom.change}</span>}
      </span>{' '}
      {measured < 5 && <span className="freedom-early">{freedomScoredOn(measured)}</span>}
    </>
  ) : (
    <>
      <span className="freedom-line__label">Freedom Score</span>{' '}
      <span className="freedom-line__starts">{FREEDOM_STARTS}</span>{' '}
    </>
  )

  // The ⓘ sits beside the row's button, not inside it: a button can't hold one.
  return (
    <div className={lineClass}>
      <div className="freedom-line__head">
        <button type="button" className="freedom-line__summary" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>
          <span className="freedom-line__text">{summary}</span>
          <Icon name="chevron-right" size={18} className="freedom-line__chevron" />
        </button>
        <InfoTip title={FREEDOM_ABOUT_TITLE} lines={FREEDOM_ABOUT} className="freedom-about" />
      </div>
      {open && <FreedomSheet freedom={freedom} summary={summary} onClose={() => setOpen(false)} />}
    </div>
  )
}

// FreedomSheet is the breakdown: what's going well and what to work on,
// then each part's points out of 20, or, before there's a score, what each
// part still needs.
function FreedomSheet({ freedom, summary, onClose }: { freedom: FreedomCard; summary: React.ReactNode; onClose: () => void }) {
  const dialogRef = React.useRef<HTMLDivElement>(null)
  const goingWellId = React.useId()
  useDialog(dialogRef, onClose)

  const body = freedom.enough ? (
    <>
      {freedom.strengths.length > 0 && (
        <div className="freedom-callout freedom-callout--well">
          <Icon name="check" size={18} className="freedom-callout__icon" />
          <div>
            <p className="freedom-callout__title" id={goingWellId}>{FREEDOM_GOING_WELL}</p>
            <ul className="freedom-callout__list" aria-labelledby={goingWellId}>
              {freedom.strengths.map((strength) => (
                <li key={strength}>{strength}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {freedom.work_on && (
        <div className="freedom-callout freedom-callout--work-on">
          <Icon name="arrow-up" size={18} className="freedom-callout__icon" />
          <div>
            <p className="freedom-callout__title">{FREEDOM_WORK_ON}</p>
            <p className="freedom-callout__text">{freedom.work_on}</p>
          </div>
        </div>
      )}
      <p className="freedom-heading">{FREEDOM_HOW_SCORED}</p>
      {/* Each part: its name and the stretch it looks at on the left,
          whole points out of 20 on the right, and a bar under both. A
          part not measured yet says why instead, with no empty bar. */}
      <ul className="freedom-parts">
        {freedom.parts.map((part) => (
          <li key={part.key}>
            <span className="freedom-part__name">
              <span className="freedom-part__label">{part.label}</span>
              <span className="freedom-part__period">{freedomPartPeriod(part.key)}</span>
            </span>
            {part.measured ? (
              <>
                <span className="freedom-part__value">
                  {freedomPoints(part.points)}
                  <span className="freedom-part__out-of">/20</span>
                </span>
                <span className="freedom-bar" aria-hidden="true">
                  <span className="freedom-bar__fill" style={{ width: `${Math.round((part.points / 20) * 100)}%` }} />
                </span>
              </>
            ) : (
              <>
                <span className="freedom-part__value freedom-part__value--none">—</span>
                <span className="freedom-part__missing">{part.missing}</span>
              </>
            )}
          </li>
        ))}
      </ul>
    </>
  ) : (
    <ul className="freedom-missing">
      {freedom.parts
        .filter((part) => !part.measured && part.missing)
        .map((part) => (
          <li key={part.key}>{part.missing}</li>
        ))}
    </ul>
  )

  return createPortal(
    <div className="sheet-overlay" onClick={onClose}>
      <div
        ref={dialogRef}
        className="sheet-content sheet-content--closable"
        role="dialog"
        aria-modal="true"
        aria-label="Freedom Score"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-handle"></div>
        <IconButton icon="close" label="Close" className="sheet-close" onClick={onClose} />
        <div className="sheet-header sheet-header--closable freedom-sheet__header">
          <span className="freedom-line__text">{summary}</span>
        </div>
        <div className="sheet-body freedom-sheet__body">{body}</div>
      </div>
    </div>,
    document.body,
  )
}
